import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ApplicationInsightsService } from 'app/shared/services/application-insights.service';
import { LoadingService } from 'app/shared/utilities/loading.service';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap, tap } from 'rxjs/operators';
import { ProfileActions } from '.';

import { IUserProfile } from '../models';
import { ProfileApiService } from '../services/remote/profile-api.service';

@Injectable()
export class ProfileEffects {
    debounceTimeInMilliseconds: number = 200;

    loadProfile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileActions.loadProfile),
            exhaustMap(() => {
                this.loadingService.addLoading(profileLoadingString);
                return this.apiService.getProfile().pipe(
                    map((profile: IUserProfile) => {
                        this.loadingService.removeLoading(profileLoadingString);
                        return ProfileActions.getProfile({ profile });
                    }),
                    catchError((error: Error) => {
                        this.loadingService.removeLoading(profileLoadingString);
                        return of(ProfileActions.apiErrorAction({ method: 'getProfile', error: JSON.stringify(error) }));
                    })
                );
            })
        )
    );

    saveUserProfile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileActions.saveUserProfile),
            exhaustMap((action) => {
                this.loadingService.addLoading(saveProfileLoadingString);
                return this.apiService.saveUserProfile(action.profile).pipe(
                    switchMap((response) => {
                        this.loadingService.removeLoading(saveProfileLoadingString);
                        if (!response.success) {
                            return [ProfileActions.apiErrorAction({ method: 'saveUserProfile', error: `${response}` })];
                        } else {
                            return [ProfileActions.saveUserProfileSuccess(), ProfileActions.loadProfile()];
                        }
                    }),
                    catchError((error: Error) => {
                        this.loadingService.removeLoading(saveProfileLoadingString);
                        return of(ProfileActions.apiErrorAction({ method: 'saveUserProfile', error: JSON.stringify(error) }));
                    })
                );
            })
        )
    );

    apiErrorAction$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ProfileActions.apiErrorAction),
                tap((action) => {
                    this.applicationInsights.trackException(JSON.parse(JSON.stringify(action.error)), [{ key: 'methodName', value: action.method }]);
                })
            ),
        { dispatch: false }
    );
    constructor(
        private readonly actions$: Actions,
        private apiService: ProfileApiService,
        private readonly applicationInsights: ApplicationInsightsService,
        private readonly loadingService: LoadingService
    ) {}
}

export const profileLoadingString: string = 'load-profile';
export const saveProfileLoadingString: string = 'save-profile';
